// @flow
import React, {useState, useEffect} from "react";
import {Modal, Form, Select, Input, Button, Row, Col, message, Spin} from "antd";
import {useTranslation} from "react-i18next";
import {courierContractService} from "../../../../services/courierContractService";

const {Option} = Select;
const {TextArea} = Input;

type Props = {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    courierId: string;
    courierData: Object;
};

export const CreateContractModal = ({visible, onCancel, onSuccess, courierId, courierData}: Props) => {
    const {t} = useTranslation(["courierContracts"]);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateFields, setTemplateFields] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [previewContent, setPreviewContent] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);

    // Kurye bilgilerini form'a pre-fill et
    useEffect(() => {
        if (courierData && form && visible) {
            form.setFieldsValue({
                fullname: courierData.fullname,
                email: courierData.email,
                cellPhone: courierData.cellPhone,
                tckn: courierData.tckn,
                address: courierData.address,
                iban: courierData.iban,
            });
        }
    }, [courierData, form, visible]);

    // Şablonları yükle
    useEffect(() => {
        if (visible) {
            loadTemplates();
        }
    }, [visible]);

    // Form değerleri değiştiğinde preview'i güncelle
    useEffect(() => {
        if (selectedTemplate && templateFields.length > 0) {
            const formValues = form.getFieldsValue();
            const filledFields = templateFields.filter(field => {
                const fieldName = field.key.replace("courier.", "");
                return formValues[fieldName] && formValues[fieldName].trim() !== "";
            });
            
            if (filledFields.length === templateFields.filter(f => f.required).length) {
                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nTüm gerekli alanlar dolduruldu!\n\nSözleşme oluşturulmaya hazır.`);
            } else {
                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nBu sözleşme şablonu için gerekli alanlar doldurulduktan sonra önizleme burada görünecektir.\n\nDoldurulan alanlar: ${filledFields.length}/${templateFields.filter(f => f.required).length}`);
            }
        }
    }, [selectedTemplate, templateFields, form]);

    const loadTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const response = await courierContractService.getCourierContractTemplates(courierId);
            if (response.data && response.data.success) {
                setTemplates(response.data.result.data || []);
            } else {
                console.error("Template loading failed:", response);
                setTemplates([]);
            }
        } catch (error) {
            console.error("Template loading error:", error);
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleTemplateChange = (templateId) => {
        const template = templates.find(t => t._id === templateId);
        setSelectedTemplate(template);
        
        // Sadece preview'ı sıfırla, form'u sıfırlama (otomatik doldurma korunsun)
        setPreviewUrl(null);
        setPreviewContent(`Sözleşme Önizlemesi: ${template?.displayName || 'Seçilen Template'}\n\nBu sözleşme şablonu için gerekli alanlar doldurulduktan sonra önizleme burada görünecektir.`);
        
        // Template'ten gerekli alanları çıkar (burada basit bir mapping yapıyoruz)
        // Gerçek implementasyonda backend'den template field'ları alınacak
        const fields = [
            {key: "courier.fullname", label: t("fields.fullname"), required: true},
            {key: "courier.email", label: t("fields.email"), required: true},
            {key: "courier.cellPhone", label: t("fields.cellPhone"), required: true},
            {key: "courier.tckn", label: t("fields.tckn"), required: true},
            {key: "courier.address", label: t("fields.address"), required: true},
            {key: "courier.iban", label: t("fields.iban"), required: false},
            {key: "today", label: t("fields.today"), required: true},
        ];
        setTemplateFields(fields);
    };

    const generatePreview = async () => {
        setLoadingPreview(true);
        try {
            const formValues = await form.validateFields();
            const response = await courierContractService.generateContractPreview(
                courierId,
                formValues.templateId,
                formValues
            );
            if (response.data && response.data.success) {
                setPreviewUrl(response.data.result.previewUrl);
                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate?.displayName || 'Seçilen Template'}\n\nÖnizleme oluşturuldu! PDF görüntüleniyor...`);
            } else {
                message.error("Önizleme oluşturulamadı");
            }
        } catch (error) {
            console.error("Preview generation error:", error);
            message.error("Önizleme oluşturulurken hata oluştu");
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Form değerlerini backend formatına çevir
            const fieldValues = {};
            Object.keys(values).forEach(key => {
                if (key !== "templateId") {
                    fieldValues[key] = values[key];
                }
            });

            const response = await courierContractService.createCourierContract(
                courierId,
                values.templateId,
                fieldValues
            );

            if (response.data && response.data.success) {
                message.success(t("messages.contractCreated"));
                onSuccess();
                form.resetFields();
                setSelectedTemplate(null);
                setTemplateFields([]);
            } else {
                message.error(response.data?.message || "Sözleşme oluşturulurken hata oluştu");
            }
        } catch (error) {
            console.error("Contract creation error:", error);
            message.error(error.message || "Sözleşme oluşturulurken hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedTemplate(null);
        setTemplateFields([]);
        setPreviewUrl("");
        setPreviewContent("");
        onCancel();
    };

    return (
        <Modal
            title={t("modal.createContract")}
            visible={visible}
            onCancel={handleCancel}
            width={800}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    {t("modal.cancel")}
                </Button>,
                <Button
                    key="create"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!selectedTemplate}
                >
                    {t("modal.create")}
                </Button>,
            ]}
        >
            <Spin spinning={loadingTemplates}>
                <Form form={form} layout="vertical" onValuesChange={(changedValues, allValues) => {
                    // Form değerleri değiştiğinde preview'i güncelle
                    if (selectedTemplate && templateFields.length > 0) {
                        const filledFields = templateFields.filter(field => {
                            const fieldName = field.key.replace("courier.", "");
                            return allValues[fieldName] && allValues[fieldName].trim() !== "";
                        });
                        
                        if (filledFields.length === templateFields.filter(f => f.required).length) {
                            setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nTüm gerekli alanlar dolduruldu!\n\nSözleşme oluşturulmaya hazır.`);
                        } else {
                            setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nBu sözleşme şablonu için gerekli alanlar doldurulduktan sonra önizleme burada görünecektir.\n\nDoldurulan alanlar: ${filledFields.length}/${templateFields.filter(f => f.required).length}`);
                        }
                    }
                }}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="templateId"
                                label={t("modal.selectTemplate")}
                                rules={[{required: true, message: t("modal.noTemplateSelected")}]}
                            >
                                <Select
                                    placeholder={t("modal.selectTemplate")}
                                    onChange={handleTemplateChange}
                                    loading={loadingTemplates}
                                >
                                    {templates.map(template => (
                                        <Option key={template._id} value={template._id}>
                                            {template.displayName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {selectedTemplate && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <h4>{t("modal.requiredFields")}</h4>
                                </Col>
                            </Row>
                            
                            <Row gutter={16}>
                                {templateFields.map(field => (
                                    <Col span={12} key={field.key}>
                                        <Form.Item
                                            name={field.key.replace("courier.", "")}
                                            label={field.label}
                                            rules={[
                                                {
                                                    required: field.required,
                                                    message: `${field.label} gereklidir`,
                                                },
                                            ]}
                                        >
                                            {field.key === "courier.address" ? (
                                                <TextArea 
                                                    rows={3} 
                                                    onChange={() => {
                                                        // Form değeri değiştiğinde preview'i güncelle
                                                        setTimeout(() => {
                                                            const formValues = form.getFieldsValue();
                                                            const filledFields = templateFields.filter(f => {
                                                                const fieldName = f.key.replace("courier.", "");
                                                                return formValues[fieldName] && formValues[fieldName].trim() !== "";
                                                            });
                                                            
                                                            if (filledFields.length === templateFields.filter(f => f.required).length) {
                                                                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nTüm gerekli alanlar dolduruldu!\n\nSözleşme oluşturulmaya hazır.`);
                                                            } else {
                                                                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nBu sözleşme şablonu için gerekli alanlar doldurulduktan sonra önizleme burada görünecektir.\n\nDoldurulan alanlar: ${filledFields.length}/${templateFields.filter(f => f.required).length}`);
                                                            }
                                                        }, 100);
                                                    }}
                                                />
                                            ) : (
                                                <Input 
                                                    onChange={() => {
                                                        // Form değeri değiştiğinde preview'i güncelle
                                                        setTimeout(() => {
                                                            const formValues = form.getFieldsValue();
                                                            const filledFields = templateFields.filter(f => {
                                                                const fieldName = f.key.replace("courier.", "");
                                                                return formValues[fieldName] && formValues[fieldName].trim() !== "";
                                                            });
                                                            
                                                            if (filledFields.length === templateFields.filter(f => f.required).length) {
                                                                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nTüm gerekli alanlar dolduruldu!\n\nSözleşme oluşturulmaya hazır.`);
                                                            } else {
                                                                setPreviewContent(`Sözleşme Önizlemesi: ${selectedTemplate.displayName}\n\nBu sözleşme şablonu için gerekli alanlar doldurulduktan sonra önizleme burada görünecektir.\n\nDoldurulan alanlar: ${filledFields.length}/${templateFields.filter(f => f.required).length}`);
                                                            }
                                                        }, 100);
                                                    }}
                                                />
                                            )}
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <h4>{t("modal.preview")}</h4>
                                    <div style={{marginBottom: "16px"}}>
                                        <Button 
                                            type="primary" 
                                            onClick={generatePreview}
                                            loading={loadingPreview}
                                            disabled={!selectedTemplate}
                                        >
                                            Önizleme Oluştur
                                        </Button>
                                    </div>
                                    <div
                                        style={{
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                            padding: "16px",
                                            minHeight: "200px",
                                            backgroundColor: "#fafafa",
                                        }}
                                    >
                                        {previewUrl ? (
                                            <iframe
                                                src={previewUrl}
                                                width="100%"
                                                height="500px"
                                                style={{
                                                    border: "none",
                                                    borderRadius: "4px",
                                                }}
                                                title="Contract Preview"
                                            />
                                        ) : (
                                            <div style={{
                                                whiteSpace: "pre-line",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "200px",
                                                color: "#999",
                                            }}>
                                                {previewContent || t("modal.loading")}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};
